import { useWaUnread } from "../../hooks/useWaUnread";
import { useApprovalsCount } from "../../hooks/useApprovalsCount";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { isDesktop } from "../../desktop/desktop";
import { buildMenu, itemActive as isItemActive, type MenuItem as Item } from "./menuItems";

interface SidebarMenuProps {
  theme: "light" | "dark";
  setTheme: (t: "light" | "dark") => void;
  user: any;
  isOpen?: boolean;
  onClose?: () => void;
}

const GROUPS_KEY = "sidebar.groups";

export default function SidebarMenu({ theme, setTheme, user, isOpen, onClose }: SidebarMenuProps) {
  const { pathname } = useLocation();
  const perms = usePermissions();
  const waUnread = useWaUnread();
  const approvals = useApprovalsCount(user?.role === "ADMIN");

  useEffect(() => {
    document.body.classList.remove("theme-light", "theme-dark");
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  // Пока меню открыто на телефоне, страница под ним не должна прокручиваться
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleLink = () => {
    if (onClose) onClose();
  };

  const { top, groups, system } = buildMenu(user, perms, { wa: waUnread, approvals });

  // «/pos» не должен подсвечиваться на «/pos-report», поэтому сравниваем по границе пути
  const itemActive = (it: Item) => isItemActive(it, pathname);
  const visible = (items: Item[]) => items.filter((i) => i.show);

  // Какие группы свёрнуты, помним в браузере; группа с открытой страницей всегда раскрыта
  const [closed, setClosed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(GROUPS_KEY) || "{}") || {};
    } catch {
      return {};
    }
  });
  const toggle = (key: string) => {
    setClosed((c) => {
      const next = { ...c, [key]: !c[key] };
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* без хранилища просто не запоминаем */
      }
      return next;
    });
  };

  const activeGroup = useMemo(() => {
    for (const g of groups) if (g.items.some((i) => i.show && isItemActive(i, pathname))) return g.key;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user?.role, perms]);

  const renderItem = (it: Item) => (
    <Link key={it.to} to={it.to} className={`sidebar-item ${itemActive(it) ? "active" : ""}`} onClick={handleLink}>
      <span className="sidebar-icon">{it.icon}</span>
      <span className="sidebar-label">{it.label}</span>
      {it.badge ? <span className="sidebar-badge">{it.badge}</span> : null}
    </Link>
  );

  return (
    <aside className={`sidebar-menu ${isOpen ? "sidebar-open" : ""}`}>
      <div className="sidebar-items">
        {visible(top).map(renderItem)}

        {groups.map((g) => {
          const items = visible(g.items);
          if (!items.length) return null;
          const open = activeGroup === g.key || !closed[g.key];
          return (
            <div key={g.key} className="sidebar-group">
              <button type="button" className={`sidebar-group-head ${activeGroup === g.key ? "has-active" : ""}`} onClick={() => toggle(g.key)} aria-expanded={open}>
                <span className="sidebar-group-title">{g.title}</span>
                <span className={`sidebar-group-chevron ${open ? "open" : ""}`} aria-hidden>▸</span>
              </button>
              {open && <div className="sidebar-group-body">{items.map(renderItem)}</div>}
            </div>
          );
        })}

        {visible(system).length > 0 && <div className="sidebar-system">{visible(system).map(renderItem)}</div>}

        {/* Установка приложения для ПК (работа без интернета, автопечать, автообновление) — в самом приложении не нужна */}
        {!isDesktop && (
          <div className="sidebar-system">
            <a className="sidebar-item" href="/desktop/" target="_blank" rel="noreferrer" onClick={handleLink}>
              <span className="sidebar-icon">🖥️</span>
              <span className="sidebar-label">Приложение для ПК</span>
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
